import React, { useState } from 'react';
import { Loader2, AlertCircle, CheckCircle2, Send } from 'lucide-react';
import type { Confidence, LeadInput } from '../types';
import { submitLead } from '../services/leads';

interface LeadFormProps {
  uprn: string;
  postcode: string;
  addressSnapshot: string;
  estimate: number | null;
  estimateLow: number | null;
  estimateHigh: number | null;
  confidence: Confidence | null;
  agentWidgetId?: string;
  brandColour?: string;
  brandName?: string;
}

export default function LeadForm({
  uprn,
  postcode,
  addressSnapshot,
  estimate,
  estimateLow,
  estimateHigh,
  confidence,
  agentWidgetId,
  brandColour,
  brandName,
}: LeadFormProps) {
  const accent = brandColour || '#f47a6b';
  const accentHover = brandColour || '#e56a5a';
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [bestTime, setBestTime] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (!email.trim() && !phone.trim()) {
      setError('Please give us either an email or a phone number so we can reach you.');
      return;
    }

    const payload: LeadInput = {
      uprn,
      postcode,
      address_snapshot: addressSnapshot,
      estimate,
      estimate_low: estimateLow,
      estimate_high: estimateHigh,
      confidence,
      contact_name: name.trim(),
      contact_email: email.trim(),
      contact_phone: phone.trim(),
      best_time_to_call: bestTime || undefined,
      notes: notes.trim() || undefined,
      agent_widget_id: agentWidgetId,
    };

    setSubmitting(true);
    try {
      const r = await submitLead(payload);
      if (!r.success) {
        setError(r.error ?? 'Something went wrong. Please try again.');
        return;
      }
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="p-6 rounded-xl bg-slate-950/60 border border-slate-800 text-center animate-fade-in">
        <CheckCircle2 size={36} className="mx-auto mb-3" style={{ color: accent }} />
        <h4 className="text-white font-semibold mb-1">Thanks — we'll be in touch.</h4>
        <p className="text-sm text-slate-400">
          {brandName
            ? `${brandName} will get back to you with a real valuation, usually within 24 hours.`
            : 'A local agent will get back to you with a real valuation, usually within 24 hours.'}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="p-6 rounded-xl bg-slate-950/60 border border-slate-800 space-y-3 text-left"
    >
      <div>
        <h4 className="text-white font-semibold mb-1">Get a real valuation</h4>
        <p className="text-xs text-slate-500">
          {brandName
            ? `${brandName} will give you an in-person valuation — usually within 24 hours.`
            : 'A local agent will give you an in-person valuation — usually within 24 hours.'}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name *"
          className="px-3 py-2.5 rounded-lg bg-slate-900/80 border border-slate-700 text-sm text-white placeholder-slate-500 focus:outline-none"
          style={{ borderColor: undefined }}
          onFocus={(e) => (e.currentTarget.style.borderColor = accent)}
          onBlur={(e) => (e.currentTarget.style.borderColor = '')}
        />
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Phone *"
          className="px-3 py-2.5 rounded-lg bg-slate-900/80 border border-slate-700 text-sm text-white placeholder-slate-500 focus:outline-none"
          onFocus={(e) => (e.currentTarget.style.borderColor = accent)}
          onBlur={(e) => (e.currentTarget.style.borderColor = '')}
        />
      </div>

      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email *"
        className="w-full px-3 py-2.5 rounded-lg bg-slate-900/80 border border-slate-700 text-sm text-white placeholder-slate-500 focus:outline-none"
        onFocus={(e) => (e.currentTarget.style.borderColor = accent)}
        onBlur={(e) => (e.currentTarget.style.borderColor = '')}
      />

      <select
        value={bestTime}
        onChange={(e) => setBestTime(e.target.value)}
        className="w-full px-3 py-2.5 rounded-lg bg-slate-900/80 border border-slate-700 text-sm text-white focus:outline-none"
      >
        <option value="">Best time to call (optional)</option>
        <option value="Morning (9am–12pm)">Morning (9am–12pm)</option>
        <option value="Afternoon (12pm–5pm)">Afternoon (12pm–5pm)</option>
        <option value="Evening (5pm–8pm)">Evening (5pm–8pm)</option>
      </select>

      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={2}
        placeholder="Anything we should know? (optional)"
        className="w-full px-3 py-2.5 rounded-lg bg-slate-900/80 border border-slate-700 text-sm text-white placeholder-slate-500 focus:outline-none resize-none"
      />

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-900/30 border border-rose-700/40 text-sm text-rose-200">
          <AlertCircle size={14} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-white font-semibold disabled:opacity-50 transition-colors"
        style={{ background: accent }}
        onMouseOver={(e) => (e.currentTarget.style.background = accentHover)}
        onMouseOut={(e) => (e.currentTarget.style.background = accent)}
      >
        {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        Request my valuation
      </button>

      <p className="text-[11px] text-slate-500 text-center">
        Your details are only shared with{' '}
        {brandName ? brandName : 'a local estate agent'}. See our{' '}
        <a href="https://rehub.studio/privacy" className="underline">
          privacy policy
        </a>
        .
      </p>
    </form>
  );
}
